import Button from "@/components/common/Button";
import FloatingFAQButton from "@/components/common/FloatingFAQButton";
import PhoneNumberInput from "@/components/common/PhoneNumberInput";
import { useAuth } from "@/hooks/useAuth";
import { zodResolver } from "@hookform/resolvers/zod";
import { motion } from "framer-motion";
import { Building2, CheckCircle, Eye, EyeOff, Mail, Phone, Shield } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { z } from "zod";

type FormValues = {
  name: string;
  primaryEmail: string;
  secondaryEmail?: string;
  phoneNumber: string;
  password: string;
  passwordConfirm: string;
  acceptTerms: true;
};

const RegisterCompany = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { registerCompany } = useAuth();

  const [isLoading, setIsLoading] = useState(false);
  const [verificationSent, setVerificationSent] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState<string | null>(null);

  // Create schema with translated messages
  const createSchema = () =>
    z
      .object({
        name: z
          .string()
          .min(2, t("auth.companyNameRequired", "Company name is required")),
        primaryEmail: z
          .string()
          .email(t("auth.invalidEmail", "Invalid email address")),
        secondaryEmail: z
          .string()
          .email(t("auth.invalidEmail", "Invalid email address"))
          .optional()
          .or(z.literal("")),
        phoneNumber: z
          .string()
          .min(1, t("auth.phoneNumberRequired", "Phone number is required"))
          .regex(
            /^\+[1-9]\d{1,14}$/,
            t(
              "auth.invalidPhoneNumber",
              "Phone number must be in international format (e.g., +251912345678)"
            )
          ),
        password: z
          .string()
          .min(
            8,
            t(
              "auth.passwordMinLength",
              "Password must be at least 8 characters"
            )
          )
          .regex(
            /[A-Z]/,
            t(
              "auth.passwordUppercase",
              "Must contain at least one uppercase letter"
            )
          )
          .regex(
            /[a-z]/,
            t(
              "auth.passwordLowercase",
              "Must contain at least one lowercase letter"
            )
          )
          .regex(
            /[0-9]/,
            t("auth.passwordNumber", "Must contain at least one number")
          )
          .regex(
            /[^A-Za-z0-9]/,
            t(
              "auth.passwordSpecial",
              "Must contain at least one special character"
            )
          ),
        passwordConfirm: z.string(),
        acceptTerms: z.literal(true, {
          errorMap: () => ({
            message: t(
              "auth.acceptTermsRequired",
              "You must accept the terms and conditions"
            ),
          }),
        }),
      })
      .refine((data) => data.password === data.passwordConfirm, {
        message: t("auth.passwordsDontMatch", "Passwords don't match"),
        path: ["passwordConfirm"],
      });

  const schema = createSchema();

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
  });

  const onSubmit = async (data: FormValues) => {
    try {
      setIsLoading(true);

      const registrationData = {
        name: data.name,
        primaryEmail: data.primaryEmail,
        secondaryEmail: data.secondaryEmail,
        phoneNumber: data.phoneNumber,
        password: data.password,
        passwordConfirm: data.passwordConfirm,
      };

      await registerCompany(registrationData);

      setRegisteredEmail(data.primaryEmail);
      setVerificationSent(true);
    } catch (err: any) {
      const backendMsg = err?.response?.data?.message || err?.message;
      const statusCode = err?.response?.status;
      if (backendMsg) {
        if (backendMsg.includes("already exists")) {
          toast.error(t("auth.emailAlreadyExists"));
        } else if (/^\d{3}$/.test(backendMsg.trim())) {
          toast.error(t("auth.registrationFailed"));
        } else {
          toast.error(backendMsg);
        }
      } else if (statusCode && (statusCode === 400 || statusCode === 500)) {
        toast.error(t("auth.registrationFailed"));
      } else {
        toast.error(t("auth.registrationFailed"));
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (verificationSent) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-primary-50 via-primary-100/50 to-primary-200/50 dark:from-gray-900 dark:via-primary-900/20 dark:to-gray-900">
        <motion.div
          initial={{ opacity: 0, y: 20, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.5, ease: "easeOut" }}
          className="w-full max-w-md rounded-3xl bg-white/90 dark:bg-gray-800/90 p-8 shadow-2xl border border-emerald-200/50 dark:border-emerald-700/50 backdrop-blur-xl flex flex-col items-center"
        >
          <div className="flex flex-col items-center">
            <div className="mb-6 flex items-center justify-center rounded-full bg-gradient-to-r from-primary-400 to-primary-600 p-5 shadow-lg">
              <svg
                className="h-14 w-14 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-3 text-center">
              {t("auth.verificationEmailSent")}
            </h2>
            <p className="text-gray-600 dark:text-gray-300 mb-2 text-center text-lg">
              {t("auth.checkYourEmail")}
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-8 text-center">
              {t("auth.verificationEmailSentTo", { email: registeredEmail })}
            </p>
            <Button
              onClick={() => navigate("/login", { replace: true })}
              className="w-full bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 text-white font-semibold py-4 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
              variant="primary"
              size="lg"
            >
              {t("auth.backToLogin")}
            </Button>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-green-50 to-emerald-100 dark:from-gray-900 dark:via-slate-800 dark:to-gray-900">
      <div className="flex items-center justify-center min-h-screen p-2 sm:p-4 lg:p-6">
        <motion.div
          initial={{ opacity: 0, y: 20, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.5, ease: "easeOut" }}
          className="w-full max-w-sm sm:max-w-md lg:max-w-lg xl:max-w-xl bg-white dark:bg-gray-800 rounded-2xl sm:rounded-3xl shadow-2xl p-4 sm:p-6 lg:p-8 space-y-6 sm:space-y-8"
        >
          {/* Header Section */}
          <div className="text-center space-y-3 sm:space-y-4">
            <div className="flex justify-center">
              <div className="p-2 sm:p-3 lg:p-4 bg-gradient-to-r from-primary-500 via-primary-600 to-primary-700 rounded-xl sm:rounded-2xl lg:rounded-3xl shadow-lg">
                <Building2 className="h-6 w-6 sm:h-8 sm:w-8 lg:h-10 lg:w-10 text-white" />
              </div>
            </div>
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold bg-gradient-to-r from-gray-900 via-primary-800 to-primary-700 dark:from-white dark:via-primary-200 dark:to-primary-300 bg-clip-text text-transparent">
              {t("auth.registerCompany")}
            </h1>
            <p className="text-base sm:text-lg lg:text-xl text-gray-600 dark:text-gray-300">
              {t("auth.hasAccount")}{" "}
              <Link
                to="/login"
                className="font-semibold text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 transition-colors duration-200"
              >
                {t("auth.login")}
              </Link>
            </p>
          </div>

          {/* Form Section */}
          <form
            onSubmit={handleSubmit(onSubmit)}
            className="space-y-6 sm:space-y-8"
          >
            <div className="space-y-6 sm:space-y-8">
              {/* Company Information Section */}
              <div className="space-y-4 sm:space-y-6">
                {/* Company Name */}
                <div className="space-y-2">
                  <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                    <Building2 className="h-4 w-4 text-primary-600" />
                    <span>{t("auth.companyName")}</span>
                  </label>
                  <input
                    id="name"
                    type="text"
                    autoComplete="organization"
                    required
                    className={`w-full px-3 sm:px-4 py-3 sm:py-4 rounded-lg sm:rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white border-2 ${
                      errors.name
                        ? "border-red-500 focus:border-red-500 focus:ring-red-500/20"
                        : "border-gray-200 dark:border-gray-600 focus:border-primary-500 focus:ring-primary-500/20"
                    } focus:ring-4 outline-none transition-all duration-300 placeholder-gray-400 dark:placeholder-gray-400 shadow-sm hover:shadow-md focus:shadow-lg text-sm sm:text-base`}
                    placeholder={t("auth.companyName")}
                    {...register("name", { required: true })}
                  />
                  {errors.name && (
                    <p className="text-sm text-red-600 dark:text-red-400 flex items-center space-x-1">
                      <span className="w-1.5 h-1.5 bg-red-500 rounded-full"></span>
                      <span>{errors.name.message}</span>
                    </p>
                  )}
                </div>

                {/* Primary Email */}
                <div className="space-y-2">
                  <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                    <Mail className="h-4 w-4 text-primary-600" />
                    <span>{t("auth.primaryEmail")}</span>
                  </label>
                  <input
                    id="primaryEmail"
                    type="email"
                    autoComplete="email"
                    required
                    className={`w-full px-3 sm:px-4 py-3 sm:py-4 rounded-lg sm:rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white border-2 ${
                      errors.primaryEmail
                        ? "border-red-500 focus:border-red-500 focus:ring-red-500/20"
                        : "border-gray-200 dark:border-gray-600 focus:border-primary-500 focus:ring-primary-500/20"
                    } focus:ring-4 outline-none transition-all duration-300 placeholder-gray-400 dark:placeholder-gray-400 shadow-sm hover:shadow-md focus:shadow-lg text-sm sm:text-base`}
                    placeholder={t("auth.primaryEmail")}
                    {...register("primaryEmail", { required: true })}
                  />
                  {errors.primaryEmail && (
                    <p className="text-sm text-red-600 dark:text-red-400 flex items-center space-x-1">
                      <span className="w-1.5 h-1.5 bg-red-500 rounded-full"></span>
                      <span>{errors.primaryEmail.message}</span>
                    </p>
                  )}
                </div>

                {/* Secondary Email */}
                <div className="space-y-2">
                  <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                    <Mail className="h-4 w-4 text-primary-600" />
                    <span>
                      {t("auth.secondaryEmail")} ({t("common.optional")})
                    </span>
                  </label>
                  <input
                    id="secondaryEmail"
                    type="email"
                    autoComplete="email"
                    className={`w-full px-3 sm:px-4 py-3 sm:py-4 rounded-lg sm:rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white border-2 ${
                      errors.secondaryEmail
                        ? "border-red-500 focus:border-red-500 focus:ring-red-500/20"
                        : "border-gray-200 dark:border-gray-600 focus:border-primary-500 focus:ring-primary-500/20"
                    } focus:ring-4 outline-none transition-all duration-300 placeholder-gray-400 dark:placeholder-gray-400 shadow-sm hover:shadow-md focus:shadow-lg text-sm sm:text-base`}
                    placeholder={t("auth.secondaryEmail")}
                    {...register("secondaryEmail")}
                  />
                  {errors.secondaryEmail && (
                    <p className="text-sm text-red-600 dark:text-red-400 flex items-center space-x-1">
                      <span className="w-1.5 h-1.5 bg-red-500 rounded-full"></span>
                      <span>{errors.secondaryEmail.message}</span>
                    </p>
                  )}
                </div>
              </div>

              {/* Password Information Section */}
              <div className="space-y-4 sm:space-y-6">
                {/* Phone Number */}
                <div className="space-y-2">
                  <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                    <Phone className="h-4 w-4 text-primary-600" />
                    <span>{t("auth.phoneNumber")}</span>
                  </label>
                  <PhoneNumberInput
                    value={watch("phoneNumber") || ""}
                    onChange={(value) => setValue("phoneNumber", value || "")}
                    error={errors.phoneNumber?.message}
                    placeholder="+251912345678"
                    required
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {t("auth.phoneNumberHelp")}
                  </p>
                </div>

                {/* Password */}
                <div className="space-y-2">
                  <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                    <Shield className="h-4 w-4 text-primary-600" />
                    <span>{t("auth.password")}</span>
                  </label>
                  <div className="relative">
                    <input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      autoComplete="new-password"
                      required
                      className={`w-full px-3 sm:px-4 py-3 sm:py-4 pr-12 rounded-lg sm:rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white border-2 ${
                        errors.password
                          ? "border-red-500 focus:border-red-500 focus:ring-red-500/20"
                          : "border-gray-200 dark:border-gray-600 focus:border-primary-500 focus:ring-primary-500/20"
                      } focus:ring-4 outline-none transition-all duration-300 placeholder-gray-400 dark:placeholder-gray-400 shadow-sm hover:shadow-md focus:shadow-lg text-sm sm:text-base`}
                      placeholder={t("auth.password")}
                      {...register("password", { required: true })}
                    />
                    <button
                      type="button"
                      className="absolute top-1/2 right-4 -translate-y-1/2 text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors duration-200 p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600"
                      onClick={() => setShowPassword((prev) => !prev)}
                      tabIndex={-1}
                    >
                      {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                    </button>
                  </div>
                  {errors.password && (
                    <p className="text-sm text-red-600 dark:text-red-400 flex items-center space-x-1">
                      <span className="w-1.5 h-1.5 bg-red-500 rounded-full"></span>
                      <span>{errors.password.message}</span>
                    </p>
                  )}
                </div>

                {/* Confirm Password */}
                <div className="space-y-2">
                  <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                    <Shield className="h-4 w-4 text-primary-600" />
                    <span>{t("auth.confirmPassword")}</span>
                  </label>
                  <div className="relative">
                    <input
                      id="passwordConfirm"
                      type={showConfirmPassword ? "text" : "password"}
                      autoComplete="new-password"
                      required
                      className={`w-full px-3 sm:px-4 py-3 sm:py-4 pr-12 rounded-lg sm:rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white border-2 ${
                        errors.passwordConfirm
                          ? "border-red-500 focus:border-red-500 focus:ring-red-500/20"
                          : "border-gray-200 dark:border-gray-600 focus:border-primary-500 focus:ring-primary-500/20"
                      } focus:ring-4 outline-none transition-all duration-300 placeholder-gray-400 dark:placeholder-gray-400 shadow-sm hover:shadow-md focus:shadow-lg text-sm sm:text-base`}
                      placeholder={t("auth.confirmPassword")}
                      {...register("passwordConfirm", { required: true })}
                    />
                    <button
                      type="button"
                      className="absolute top-1/2 right-4 -translate-y-1/2 text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors duration-200 p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600"
                      onClick={() => setShowConfirmPassword((prev) => !prev)}
                      tabIndex={-1}
                    >
                      {showConfirmPassword ? (
                        <EyeOff size={20} />
                      ) : (
                        <Eye size={20} />
                      )}
                    </button>
                  </div>
                  {errors.passwordConfirm && (
                    <p className="text-sm text-red-600 dark:text-red-400 flex items-center space-x-1">
                      <span className="w-1.5 h-1.5 bg-red-500 rounded-full"></span>
                      <span>{errors.passwordConfirm.message}</span>
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Terms and Conditions */}
            <div className="space-y-4 pt-6 border-t-2 border-gray-200 dark:border-gray-600">
              <div className="flex items-start space-x-3">
                <input
                  id="acceptTerms"
                  type="checkbox"
                  className={`h-5 w-5 rounded border-2 text-primary-600 focus:ring-4 focus:ring-primary-500/20 focus:ring-offset-2 focus:ring-offset-white dark:focus:ring-offset-gray-800 transition-all duration-200 ${
                    errors.acceptTerms
                      ? "border-red-500"
                      : "border-gray-300 dark:border-gray-600"
                  }`}
                  {...register("acceptTerms", { required: true })}
                />
                <label
                  htmlFor="acceptTerms"
                  className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed"
                >
                  {t("auth.acceptTerms")}{" "}
                  <a
                    href="#"
                    className="font-semibold text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 transition-colors duration-200"
                  >
                    {t("auth.termsOfService")}
                  </a>{" "}
                  {t("auth.and")}{" "}
                  <a
                    href="#"
                    className="font-semibold text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 transition-colors duration-200"
                  >
                    {t("auth.privacyPolicy")}
                  </a>
                </label>
              </div>
              {errors.acceptTerms && (
                <p className="text-sm text-red-600 dark:text-red-400 flex items-center space-x-1">
                  <span className="w-1.5 h-1.5 bg-red-500 rounded-full"></span>
                  <span>{errors.acceptTerms.message}</span>
                </p>
              )}
            </div>

            {/* Submit Button */}
            <div className="pt-4">
              <button
                type="submit"
                disabled={isLoading}
                className="w-full py-3 sm:py-4 rounded-lg sm:rounded-xl bg-gradient-to-r from-primary-600 via-primary-700 to-primary-800 text-white font-bold shadow-lg hover:from-primary-700 hover:via-primary-800 hover:to-primary-900 focus:ring-4 focus:ring-primary-500/30 focus:ring-offset-2 focus:ring-offset-white dark:focus:ring-offset-gray-800 transition-all duration-300 text-base sm:text-lg disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-[1.02] active:scale-[0.98]"
              >
                {isLoading ? (
                  <div className="flex items-center justify-center space-x-3">
                    <div className="w-6 h-6 border-3 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>{t("common.loading", "Loading...")}</span>
                  </div>
                ) : (
                  <div className="flex items-center justify-center space-x-2">
                    <CheckCircle className="h-5 w-5" />
                    <span>{t("auth.register")}</span>
                  </div>
                )}
              </button>
            </div>
          </form>
        </motion.div>
      </div>

      {/* Floating FAQ Button */}
      <FloatingFAQButton />
    </div>
  );
};

export default RegisterCompany;
